import {
  Container,
  InteractionEvent,
  Sprite,
  WRAP_MODES,
  filters,
  Circle,
} from "pixi.js";
import { singleton } from "tsyringe";
import { EventBus } from "~/src/event-bus";
import { PixiApp } from "~/src/pixi-app";
import gsap from "gsap";
import { GlowFilter } from "pixi-filters";
import clamp from "lodash/clamp";
import { MAIN_COLOR } from "~/src/config/colors";

const GLOW_STRENGTH_ACTIVE = 6;
const GLOW_STRENGTH_REST = 3;
const GLOW_ANIM_DUR = 0.75;
const DISP_SCALE = 20;
const DISP_PAD = 10;
const DISP_SPEED = 0.425;
// const y = document.documentElement.clientHeight - 100;
@singleton()
export class DraggerComponent {
  private isDragging = false;
  isMorphing = false;
  private isHovered = false;
  private glowTween: gsap.core.Tween;
  private dispScaleTween: gsap.core.Tween;
  private dragger: Sprite;
  private dispFilter: any;
  private dispSprite: Sprite;
  private glow: GlowFilter;
  constructor(private eventBus: EventBus, private pixiApp: PixiApp) {}
  mount(container: Container) {
    const dragger = new Sprite(this.pixiApp.loader.resources.circle.texture!);
    dragger.scale.set(0.5, 0.5);
    this.dragger = dragger;
    this.createGlow();
    this.createDisplacementFilter();
    dragger.interactive = true;
    dragger.anchor.set(0.5, 0.5);
    dragger.hitArea = new Circle(0, 0, dragger.width);
    dragger.cursor = "grab";
    dragger.y = document.documentElement.clientHeight - dragger.height / 2 - 75;
    dragger.filters = [this.dispFilter, this.glow];
    dragger.x = document.documentElement.clientWidth / 2;
    dragger.addChild(this.dispSprite);
    container.addChild(dragger);
    this.setupEvents();
  }
  disableInteraction() {
    this.dragger.interactive = false;
  }
  enableInteraction() {
    this.dragger.interactive = true;
  }
  playEnterAnimation() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_REST,
      innerStrength: GLOW_STRENGTH_REST,
      duration: 1.5,
    });
  }
  nilGlowStrength() {
    this.glow.innerStrength = 0;
    this.glow.outerStrength = 0;
  }
  private onTick = () => {
    this.dispSprite.x += DISP_SPEED;
    // this.dispSprite.y += DISP_SPEED;
  };
  private enableMorphing() {
    if (this.isMorphing) return;
    this.isMorphing = true;
    this.pixiApp.ticker.add(this.onTick);
    this.dispScaleTween?.kill();
    this.dispScaleTween = gsap.to(this.dispFilter.scale, {
      x: DISP_SCALE,
      y: DISP_SCALE,
    });
  }
  private disableMorphing() {
    this.isMorphing = false;
    this.dispScaleTween?.kill();
    this.dispScaleTween = gsap.to(this.dispFilter.scale, {
      x: 0,
      y: 0,
    });
    this.pixiApp.ticker.remove(this.onTick);
  }

  private createDisplacementFilter() {
    const sprite = new Sprite(this.pixiApp.loader.resources.textDisp.texture!);
    sprite.texture.baseTexture.wrapMode = WRAP_MODES.MIRRORED_REPEAT;
    const filter = new filters.DisplacementFilter(sprite);
    this.dispFilter = filter;
    this.dispSprite = sprite;
    filter.padding = DISP_PAD;
    filter.scale.set(0, 0);
  }
  private createGlow() {
    const glow = new GlowFilter({
      color: MAIN_COLOR,
      knockout: true,
      outerStrength: 0,
      innerStrength: 0,
      quality: 1,
    });
    glow.padding = 25;
    this.glow = glow;
  }
  private setupEvents() {
    this.eventBus.on("resize", this.onResize);
    this.dragger
      .on("pointerover", this.onPointerOver)
      .on("pointerout", this.onPointerOut)
      .on("pointerdown", this.onPointerDown)
      .on("pointerup", this.onPointerUp)
      .on("pointerupoutside", this.onPointerUp);
  }
  private onPointerMove = (e: InteractionEvent) => {
    const viewportWidth = document.documentElement.clientWidth;

    const x = clamp(e.data.global.x, 0, viewportWidth);
    const y = clamp(e.data.global.y, 0, document.documentElement.clientHeight);
    const progress = x / viewportWidth;
    this.eventBus.emit("dragger/progress", progress);
    gsap.to(this.dragger, {
      x,
      y,
    });
  };
  private onResize = () => {
    const { clientWidth, clientHeight } = document.documentElement;
    const { x, y } = this.dragger;
    if (x <= 0 || x >= clientWidth) {
      this.dragger.x = clientWidth / 2;
    }
    if (y <= 0 || y >= clientHeight) {
      this.dragger.y = clientHeight - this.dragger.height / 2 - 75;
    }
  };
  private onPointerDown = () => {
    this.enableMorphing();
    this.isDragging = true;
    this.eventBus.emit("dragger/grab");
    document.body.classList.add("dragging");
    this.dragger.cursor = "grabbing";
    this.dragger.on("pointermove", this.onPointerMove);
    this.animateGlowActive();
  };
  private onPointerUp = () => {
    if (!this.isHovered) {
      this.disableMorphing();
    }
    this.eventBus.emit("dragger/release");
    document.body.classList.remove("dragging");
    this.dragger.cursor = "grab";
    this.dragger.off("pointermove", this.onPointerMove);
    this.isDragging = false;
    if (!this.isHovered) {
      this.animateGlowRest();
    }
  };
  private animateGlowActive() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_ACTIVE,
      innerStrength: GLOW_STRENGTH_ACTIVE,
      duration: GLOW_ANIM_DUR,
      ease: "linear",
    });
  }
  private animateGlowRest() {
    this.glowTween?.kill();
    this.glowTween = gsap.to(this.glow, {
      outerStrength: GLOW_STRENGTH_REST,
      innerStrength: GLOW_STRENGTH_REST,
      duration: GLOW_ANIM_DUR,
      ease: "linear",
    });
  }
  private onPointerOut = () => {
    this.isHovered = false;
    this.eventBus.emit("dragger/pointerOut");
    if (!this.isDragging) {
      this.disableMorphing();
      this.animateGlowRest();
    }
  };
  private onPointerOver = () => {
    this.isHovered = true;
    if (!this.isDragging) {
      this.enableMorphing();
    }
    this.animateGlowActive();
    this.eventBus.emit("dragger/pointerOver");
  };
}
